import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { InviteModal } from "./InviteModal";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProfileHeader } from "./profile-card/ProfileHeader";
import { ProfileBadges } from "./profile-card/ProfileBadges";

interface ProfileCardProps {
  id: string;
  fullName: string | null;
  headline: string | null;
  avatarUrl: string | null;
  categoryName?: string;
  isHiring?: boolean;
  languages?: string[];
  availabilityStatus?: string;
  experienceLevel?: string;
}

export const ProfileCard = ({ 
  id, 
  fullName, 
  headline, 
  avatarUrl, 
  categoryName,
  isHiring = false,
  languages = [],
  availabilityStatus = 'available',
  experienceLevel
}: ProfileCardProps) => {
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { data: isCastingDirector } = useQuery({
    queryKey: ['isCastingDirector', id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data } = await supabase
        .from('profiles')
        .select('artist_categories(name)')
        .eq('id', user.id)
        .single();

      return data?.artist_categories?.name === 'Casting Directors';
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      e.stopPropagation();
      return;
    }
    navigate(`/profile/${id}`);
  };

  return (
    <>
      <Card 
        className="hover:shadow-lg transition-shadow cursor-pointer relative flex flex-col h-full" 
        onClick={handleClick}
      >
        <CardHeader>
          <ProfileHeader
            fullName={fullName}
            headline={headline}
            avatarUrl={avatarUrl}
            categoryName={categoryName}
            isHiring={isHiring}
            availabilityStatus={availabilityStatus}
          />
        </CardHeader>
        <CardContent className="flex flex-col flex-grow">
          <div className="flex-grow">
            <p className="text-sm text-muted-foreground mb-4">
              {headline || 'No headline provided'}
            </p>
            
            <ProfileBadges
              languages={languages}
              experienceLevel={experienceLevel}
            />
          </div>

          {isCastingDirector && categoryName !== 'Casting Directors' && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInviteModal(true);
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite to Opportunity
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        artistId={id}
        artistName={fullName || 'Artist'}
      />
    </>
  );
};